<template>
  <section :data-e2e="TaxCypressAttribute.probateCalculator.views.ONTARIO">
    <ProbateTaxDescriptionContent
      v-if="showDescription"
      :title="title"
      :subtitle="subtitle"
      :cost-description="costDescription"
      @scroll-to-calculator="scrollToCalculator"
      :data-e2e="TaxCypressAttribute.probateCalculator.views.DESCRIPTION"
    />
    <section ref="calculator" class="is-justify-content-center columns">
      <div class="column is-justify-content-center is-5 px-6 py-5">
        <div class="has-border-light has-border-radius px-6 py-5">
          <div class="mb-6">
            <h2 class="is-size-2 has-text-centered has-text-weight-semibold m-b-10 is-capitalized">
              {{ $t('tax.probate-tax-calculator') }}
            </h2>
            <p class="m-b-20 m-l-2 has-text-weight-medium">
              {{ $t('tax.probate-calculator-ontario-calculator-subtitle') }}
            </p>
          </div>

          <ValidationObserver ref="validationObserver" v-slot="{ invalid }">
            <FormCurrency
              v-model.number="probateTaxData.inputs.assetsValue"
              :rules="{ required: true, min_value: 0 }"
              icon="currency-dollar"
              :placeholder="$t('accounting.assets')"
              :show-currency-select="false"
              :currency="PredefinedCurrency.CAD.id"
              :has-fixed-label="true"
              :data-e2e="TaxCypressAttribute.probateCalculator.inputs.ASSET_VALUE"
            >
              <template #subLabel>
                <div class="is-flex is-align-content-center">
                  <i class="icon cls cls-alert-circle mr-1"></i>
                  <small>{{ $t('tax.probate-calculator-include-all-assets') }}</small>
                </div>
              </template>
            </FormCurrency>
            <FormCurrency
              v-model.number="probateTaxData.inputs.liabilitiesValue"
              :rules="{ required: true, min_value: 0 }"
              icon="currency-dollar"
              :placeholder="$t('accounting.liabilities')"
              :show-currency-select="false"
              :currency="PredefinedCurrency.CAD.id"
              :has-fixed-label="true"
              :data-e2e="TaxCypressAttribute.probateCalculator.inputs.LIABILITY_VALUE"
            >
              <template #subLabel>
                <div class="is-flex is-align-content-center">
                  <i class="icon cls cls-alert-circle mr-1"></i>
                  <small>{{ $t('tax.probate-calculator-include-all-liabilities') }}</small>
                </div>
              </template>
            </FormCurrency>
            <div class="is-flex is-justify-content-center">
              <ButtonView
                class="button m-b-20"
                :label="$t('tax.calculate')"
                type="is-primary"
                :expanded="true"
                :disabled="invalid"
                @click="calculateProbateTax"
                :data-e2e="TaxCypressAttribute.probateCalculator.buttons.ESTIMATE"
              ></ButtonView>
            </div>
          </ValidationObserver>

          <section class="mt-5">
            <div class="columns">
              <div class="column">
                <h2 class="is-size-2 has-text-centered has-text-weight-semibold m-b-10">
                  {{ $t('tax.probate-estimated-tax') }}
                </h2>
              </div>
            </div>
            <div class="columns is-justify-content-center">
              <div
                class="column is-11 has-background-secondary has-text-centered has-border-radius py-4 is-size-2 has-text-weight-semibold"
              >
                {{ animatedAmountObject.formattedAmount }}
              </div>
            </div>
          </section>

          <section class="mt-5 pt-4">
            <ProbateCalculatorBreakdown :line-items="probateTaxData.lineItems" />
          </section>
        </div>
      </div>
      <LoadingView :active="isLoading" />
    </section>
  </section>
</template>

<script>
import FormCurrency from '@/modules/core/form/components/FormCurrency.vue';
import ValidationObserver from '/@/modules/core/form/components/ValidationObserver.vue';
import { TaxCypressAttribute } from '@/modules/tax/constants/TaxCypressAttribute';
import { PredefinedJurisdiction } from '@/modules/predefined/enums/PredefinedJurisdiction';
import { HasCalculatorMixin } from '@/modules/tax/mixins/HasCalculatorMixin';
import ProbateTaxDescriptionContent from '@/modules/tax/components/calculator/ProbateTaxDescriptionContent.vue';
import Lang from '@/modules/core/app/services/Lang';
import ProbateCalculatorBreakdown from '@/modules/tax/components/calculator/ProbateCalculatorBreakDown.vue';
import { Calculator } from '@/modules/tax/models/calculators/Calculator';
import TaxCalculatorService from '@/modules/tax/services/CalculatorService';
import { PredefinedCurrency } from '@/modules/predefined/enums/PredefinedCurrency';
import { AnimatedTaxAmount } from '@/modules/tax/models/calculators/AnimatedTaxAmount';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import LoadingView from '@/modules/core/ui/components/wrappers/LoadingView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'OntarioProbateTaxCalculator',
  mixins: [HasCalculatorMixin],
  components: {
    ButtonView,
    ProbateCalculatorBreakdown,
    ProbateTaxDescriptionContent,
    ValidationObserver,
    FormCurrency,
    LoadingView
  },
  props: {
    showDescription: {
      type: Boolean,
      default: true
    }
  },
  static: {
    TaxCypressAttribute,
    PredefinedCurrency
  },
  local() {
    return {
      title: Lang.t('tax.ontario-probate-calculator'),
      subtitle: Lang.t('tax.probate-calculator-subtitle'),
      costDescription: Lang.t('tax.ontario-probate-calculator-costs')
    };
  },
  data() {
    return {
      isLoading: false,
      probateTaxData: Calculator.mapModel({
        jurisdictionId: PredefinedJurisdiction.ONTARIO.id,
        inputs: {
          assetsValue: 0,
          liabilitiesValue: 0
        }
      }),
      animatedAmountObject: new AnimatedTaxAmount({})
    };
  },

  methods: {
    async calculateProbateTax() {
      this.isLoading = true;
      try {
        this.probateTaxData = await TaxCalculatorService.calculate(this.probateTaxData);
      } finally {
        this.isLoading = false;
      }

      this.animatedAmountObject.target = Number(
        this.probateTaxData?.calculations?.estimatedProbateTax
      );
      this.animateAmount(this.animatedAmountObject);
    }
  }
};
</script>
